import { createAction, createSlice } from "@reduxjs/toolkit";
import { TabName } from "const";
import { handleErrorStopLoading } from "redux/redux-util";
import { FundTransactionType } from "const";
import cloneDeep from 'lodash/cloneDeep'
import uniqBy from 'lodash/uniqBy'
import pick from 'lodash/pick'
import isEqual from 'lodash/isEqual'
import moment from "moment-timezone";
import find from "lodash/find";

const displaySourceDataInit = {
  transaction: [],
  loading: false,
  pagination: { page: 1, rowPerPage: 10, total: null }
}

const retrospectiveInit = {
  allData: cloneDeep(displaySourceDataInit),
  buyData: cloneDeep(displaySourceDataInit),
  sellData: cloneDeep(displaySourceDataInit),
  switchData: cloneDeep(displaySourceDataInit),
}

const initialState = {
  loading: false,
  canceled: false,
  uhmfSelected: undefined,
  activeTabKey: TabName.HOLDING_TRANS,
  transactionNoSelected: null,
  transactionType: 99,
  dateFilter: [moment(), moment()],
  statusFilter: ['A'],
  holdingTrans: {
    histories: [],
    total: 0,
  },
  retrospectiveTrans: cloneDeep(retrospectiveInit),
}

const slice = createSlice({
  initialState,
  name: "transactions",
  reducers: {
    initial: () => {
      return cloneDeep(initialState)
    },
    filterUhmf: (state, { payload }) => {
      state.uhmfSelected = payload
      state.retrospectiveTrans = { ...state.retrospectiveTrans ?? {}, };
    },
    onTabChange: (state, { payload }) => {
      state.activeTabKey = payload
    },
    fetchFundTrans: (state) => {
      state.loading = true
    },
    fetchFundTransSuccess: (state, { payload }) => {
      state.loading = false
      state.holdingTrans.histories = payload.holdingTrans
      state.holdingTrans.total = payload.holdingTrans.length
    },
    fetchFundTransFailure: (state) => {
      state.loading = false
    },
    onViewTrans: (state, { payload }) => {
      state.transactionNoSelected = payload
      state.canceled = false;
    },
    cancelTrans: (state) => {
      state.loading = true
    },
    cancelTransSuccess: (state) => {
      state.loading = false;
      state.canceled = true;
    },
    cancelTransFailure: handleErrorStopLoading,
    fetchRetrospectiveTrans: (state, { payload }) => {
      const criteriaField = ['uhmfId', 'fromMonth', 'fromYear', 'toMonth', 'toYear', 'transactionStatus']
      const oldCriteria = pick(state.criteria, criteriaField)
      const newCriteria = pick(payload, criteriaField)
      if (!isEqual(oldCriteria, newCriteria)) {
        state.retrospectiveTrans = cloneDeep(retrospectiveInit)
      }
      state.criteria = payload
    },
    fetchRetrospectiveTransSuccess: (state, { payload }) => {
      const sourceData = getSourceData(payload.transactionType, state)
      sourceData.loading = false;
      sourceData.transaction = uniqBy([...sourceData.transaction, ...payload.transaction], 'transactionNo');
      sourceData.pagination = { ...sourceData.pagination, total: payload.total, page: payload.page };
    },
    fetchRetrospectiveTransFailure: (state) => {
      state.retrospectiveTrans.buyData.loading = false
      state.retrospectiveTrans.sellData.loading = false
      state.retrospectiveTrans.switchData.loading = false
      state.retrospectiveTrans.allData.loading = false
    },
    fetchRetrospectiveTransStatus: (state, { payload }) => {
      const sourceData = getSourceData(payload.transactionType, state)
      sourceData.loading = payload.loading;
    },
    setTransactionTypeTab: (state, { payload }) => {
      state.transactionType = payload
    },
    setDateFilter: (state, { payload }) => {
      state.dateFilter = payload
    },
    setStatusFilter: (state, { payload: status }) => {
      if (status.length > 1 && status.includes('A')) {
        if (status.indexOf('A') === status.length - 1) {
          state.statusFilter = ['A']
        } else {
          state.statusFilter = status.filter(s => s !== 'A')
        }
      } else {
        if (!status || status.length === 0) {
          state.statusFilter = ['A']
        } else {
          state.statusFilter = status
        }
      }
    },
    canNotCancelTrans: (state, { payload: transactionNo }) => {
      const trans = find(state.holdingTrans.histories, { transactionNo })
      if (trans) trans.cancelFlag = false
    },
  },
})

export const fetchAllData = createAction(`${slice.name}/fetchAllData`)
export const fetchBuyData = createAction(`${slice.name}/fetchBuyData`)
export const fetchSellData = createAction(`${slice.name}/fetchSellData`)
export const fetchSwitchData = createAction(`${slice.name}/fetchSwitchData`)

export const {
  initial,
  filterUhmf,
  onTabChange,
  onViewTrans,
  fetchFundTrans,
  fetchFundTransSuccess,
  fetchFundTransFailure,
  fetchRetrospectiveTrans,
  fetchRetrospectiveTransSuccess,
  fetchRetrospectiveTransFailure,
  fetchRetrospectiveTransStatus,
  cancelTrans,
  cancelTransSuccess,
  cancelTransFailure,
  setTransactionTypeTab,
  setDateFilter,
  setStatusFilter,
  canNotCancelTrans,
} = slice.actions;

export default slice;

function getSourceData(transactionType, state) {
  switch (transactionType) {
    case FundTransactionType.Buy:
      return state.retrospectiveTrans.buyData;
    case FundTransactionType.Sell:
      return state.retrospectiveTrans.sellData;
    case FundTransactionType.Switch:
      return state.retrospectiveTrans.switchData;
    default:
      return state.retrospectiveTrans.allData;
  }
}
